/**
 * Content Management Page
 * Manage privacy policy, terms, and front page images
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { ToggleLeft, ToggleRight, ChevronLeft, ChevronRight, Trash2, FileText, Plus, X, Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, List, ListOrdered, Link, Image, Code, Upload, Info } from 'lucide-react';
import { realAPI } from '../api/realEndpoints';
import { formatDate } from '../utils/helpers';
import { ITEMS_PER_PAGE } from '../utils/constants';
import { useToastNotifications } from '../hooks/useToast';
import { uploadImage, validateImageFile } from '../utils/imageUpload';
import Card from '../components/Card';
import Button from '../components/Button';
import Modal from '../components/Modal';
import './ContentManagement.css';

// Utility function to clean HTML content by removing data attributes
const cleanHtmlContent = (html: string): string => {
  // Create a temporary DOM element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Remove all data attributes from all elements
  const allElements = tempDiv.querySelectorAll('*');
  allElements.forEach(element => {
    // Remove all data-* attributes
    Array.from(element.attributes).forEach(attr => {
      if (attr.name.startsWith('data-')) {
        element.removeAttribute(attr.name);
      }
    });
  });

  return tempDiv.innerHTML;
};

interface ContentItem {
  id: number;
  name: string;
  type: string;
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
  content?: string[];
}

export default function ContentManagement() {
  const [contentList, setContentList] = useState<ContentItem[]>([]);
  const [allContent, setAllContent] = useState<ContentItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState<'name' | 'type' | 'status' | 'createdAt' | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [isLoading, setIsLoading] = useState(true);
  const [isNotFound, setIsNotFound] = useState(false);
  const { success, error } = useToastNotifications();

  // Modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [contentToDelete, setContentToDelete] = useState<ContentItem | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [newContentType, setNewContentType] = useState<'term_and_condition' | 'privacy_policy' | 'other'>('term_and_condition');
  const [newContentName, setNewContentName] = useState('');
  const [editingContent, setEditingContent] = useState<ContentItem | null>(null);

  // Form validation states
  const [formErrors, setFormErrors] = useState<{
    name?: string;
    content?: string;
  }>({});

  // Rich text editor ref
  const editorRef = useRef<HTMLDivElement>(null);

  // Image upload states
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  // Rich text editor image upload states
  const [isRichTextImageModalOpen, setIsRichTextImageModalOpen] = useState(false);
  const [richTextImage, setRichTextImage] = useState<string | null>(null);
  const [isRichTextImageUploading, setIsRichTextImageUploading] = useState(false);

  /* ------------------------------ FETCH CONTENT ------------------------------ */
  const fetchContent = useCallback(async () => {
    setIsLoading(true);
    setIsNotFound(false);
    try {
      const data = await realAPI.content.getContent();
      const content = data.result || [];
      setAllContent(content);

      // Apply sorting
      const sortedContent = [...content].sort((a, b) => {
        if (sortField === 'createdAt') {
          const aTime = new Date(a.createdAt).getTime();
          const bTime = new Date(b.createdAt).getTime();
          return sortOrder === 'asc' ? aTime - bTime : bTime - aTime;
        }

        let aValue: string = '';
        let bValue: string = '';

        switch (sortField) {
          case 'name':
            aValue = a.name.toLowerCase();
            bValue = b.name.toLowerCase();
            break;
          case 'type':
            aValue = a.type.toLowerCase();
            bValue = b.type.toLowerCase();
            break;
          case 'status':
            aValue = a.status.toLowerCase();
            bValue = b.status.toLowerCase();
            break;
          default:
            aValue = a.name.toLowerCase();
            bValue = b.name.toLowerCase();
        }

        if (sortOrder === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      });

      // Apply pagination
      const startIndex = (page - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      const paginatedContent = sortedContent.slice(startIndex, endIndex);

      setContentList(paginatedContent);
      setTotal(content.length);
    } catch (error: any) {
      console.error('Failed to fetch content:', error);
      if (error.message && (error.message.includes('404') || error.message.includes('HTTP 404'))) {
        setIsNotFound(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, [page, sortField, sortOrder]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  /* ------------------------------ IMAGE UPLOAD ------------------------------ */
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateImageFile(file);
    if (validationError) {
      error(validationError);
      return;
    }

    setIsUploading(true);

    try {
      const result = await uploadImage(file);
      setUploadedImage(result.url);
    } catch (err) {
      error('Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRichTextImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateImageFile(file);
    if (validationError) {
      error(validationError);
      return;
    }

    setIsRichTextImageUploading(true);

    try {
      const result = await uploadImage(file);
      setRichTextImage(result.url);
    } catch (err) {
      error('Failed to upload image');
    } finally {
      setIsRichTextImageUploading(false);
    }
  };

  const handleInsertRichTextImage = () => {
    if (richTextImage && editorRef.current) {
      // Insert the image into the rich text editor
      const imgHtml = `<img src="${richTextImage}" alt="Content image" style="max-width: 100%; height: auto;" />`;
      document.execCommand('insertHTML', false, imgHtml);

      // Close modal and reset
      setIsRichTextImageModalOpen(false);
      setRichTextImage(null);
    }
  };

  // Table shimmer component
  const TableShimmer = () => (
    <div className="table-container">
      <div className="table-min-w">
        {/* Real Table Headers without sorting icons */}
        <div className="grid grid-cols-6 gap-3 p-4 pl-4 rounded-t-xl font-semibold text-gray-700 text-sm border-b border-gray-200 -ml-2 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm">
          <div className="text-gray-700 dark:text-white" style={{ paddingLeft: '10px' }}>Name</div>
          <div className="text-gray-700 dark:text-white">Type</div>
          <div className="text-gray-700 dark:text-white">Publish Date</div>
          <div className="text-gray-700 dark:text-white">Last Updated</div>
          <div className="text-gray-700 dark:text-white" style={{ paddingLeft: '10px' }}>Status</div>
          <div className="text-gray-700 dark:text-white text-center">Actions</div>
        </div>

        {/* Loader in table area */}
        <div className="flex flex-col items-center justify-center py-12">
          <div className="loader"></div>
        </div>
      </div>
    </div>
  );

  /* ------------------------------ FORM VALIDATION ------------------------------ */
  const validateForm = (): boolean => {
    const errors: typeof formErrors = {};

    // Validate content name
    if (!newContentName.trim()) {
      errors.name = 'Content name is required';
    } else if (newContentName.trim().length < 3) {
      errors.name = 'Content name must be at least 3 characters long';
    } else if (newContentName.trim().length > 100) {
      errors.name = 'Content name must not exceed 100 characters';
    }

    // Validate content body
    if (newContentType === 'other') {
      // For 'other' type, validate that an image is uploaded
      if (!uploadedImage) {
        errors.content = 'Please upload an image';
      }
    } else {
      // For text content types, validate the rich text editor
      const plainText = editorRef.current?.innerText || '';

      if (!plainText.trim()) {
        errors.content = 'Please add some content';
      } else if (plainText.trim().length < 10) {
        errors.content = 'Content must be at least 10 characters long';
      } else if (plainText.trim().length > 10000) {
        errors.content = 'Content must not exceed 10,000 characters';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setNewContentName('');
    setFormErrors({});
    setEditingContent(null);
    setUploadedImage(null);
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }
  };

  /* ------------------------------ RICH TEXT EDITOR FUNCTIONS ------------------------------ */
  const execCommand = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
  };

  const handleAddContent = () => {
    // Determine available types to set smart default
    const hasTerms = allContent.some(c => c.type === 'term_and_condition');
    const hasPrivacy = allContent.some(c => c.type === 'privacy_policy');

    let defaultType: 'term_and_condition' | 'privacy_policy' | 'other' = 'other';

    if (!hasTerms) {
      defaultType = 'term_and_condition';
    } else if (!hasPrivacy) {
      defaultType = 'privacy_policy';
    }

    // Reset all form states to ensure clean form
    setNewContentType(defaultType);
    setNewContentName('');
    setFormErrors({});
    setEditingContent(null);

    // Clear editor content
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
    }

    // Open the modal
    setIsAddModalOpen(true);
  };

  const handleEditContent = (content: ContentItem) => {
    setEditingContent(content);
    setNewContentType(content.type as 'term_and_condition' | 'privacy_policy' | 'other');
    setNewContentName(content.name);
    setFormErrors({});
    setIsEditModalOpen(true);

    // Handle image content for 'other' type
    if (content.type === 'other' && content.content && content.content.length > 0) {
      // For 'other' type, content[0] should be the image URL
      setUploadedImage(content.content[0]);
    } else {
      setUploadedImage(null);
    }

    // Populate editor with existing content from the content array
    setTimeout(() => {
      if (editorRef.current && content.type !== 'other') {
        if (content.content && content.content.length > 0) {
          // The content is stored as an array, take the first item
          const contentBody = content.content[0] || '';
          editorRef.current.innerHTML = contentBody;
        } else {
          editorRef.current.innerHTML = '';
        }
      }
    }, 100);
  };

  const handleConfirmEditContent = async () => {
    // Clear previous errors
    setFormErrors({});

    // Validate form
    if (!validateForm()) {
      return;
    }

    let contentData: string[];

    if (newContentType === 'other') {
      // For 'other' type, use the uploaded image URL as content
      contentData = [uploadedImage!];
    } else {
      // For text content types, use the rich text editor content
      const content = editorRef.current?.innerHTML || '';
      const cleanContent = cleanHtmlContent(content);
      contentData = [cleanContent];
    }

    try {
      // Update existing content with new content body
      await realAPI.content.updateContent(
        editingContent!.id,
        newContentType,
        contentData,
        newContentName.trim()
      );

      await fetchContent();
      setIsEditModalOpen(false);
      setEditingContent(null);
      resetForm();
      success('Content updated successfully', 'Content has been updated successfully');
    } catch (err) {
      console.error('Failed to update content:', err);
      error('Failed to update content', 'Please try again later');
    }
  };

  const handleConfirmAddContent = async () => {
    // Clear previous errors
    setFormErrors({});

    // Validation: Check if single-instance content already exists
    if (newContentType === 'term_and_condition' || newContentType === 'privacy_policy') {
      const exists = allContent.some(item => item.type === newContentType);
      if (exists) {
        error(
          `Already Exists`,
          `${newContentType === 'term_and_condition' ? 'Terms & Conditions' : 'Privacy Policy'} already exists. You can only allow one input`
        );
        return;
      }
    }

    // Validate form
    if (!validateForm()) {
      return;
    }

    let contentData: string[];

    if (newContentType === 'other') {
      // For 'other' type, use the uploaded image URL as content
      contentData = [uploadedImage!];
    } else {
      // For text content types, use the rich text editor content
      const content = editorRef.current?.innerHTML || '';
      const cleanContent = cleanHtmlContent(content);
      contentData = [cleanContent];
    }

    try {
      await realAPI.content.addContent(newContentType, contentData, newContentName.trim());

      await fetchContent();
      setIsAddModalOpen(false);
      resetForm();
      success('Content added successfully', 'New content has been created');
    } catch (err) {
      console.error('Failed to add content:', err);
      error('Failed to add content', 'Please try again later');
    }
  };

  const handleNameChange = (value: string) => {
    setNewContentName(value);
    // Clear name error when user starts typing
    if (formErrors.name) {
      setFormErrors(prev => ({ ...prev, name: undefined }));
    }
  };

  const handleContentChange = () => {
    // Clear content error when user starts typing
    if (formErrors.content) {
      setFormErrors(prev => ({ ...prev, content: undefined }));
    }
  };

  const handleToggleStatus = (content: ContentItem) => {
    const newStatus: 'active' | 'inactive' = content.status === 'active' ? 'inactive' : 'active';

    // 1. Update UI instantly
    setContentList((prev) =>
      prev.map((c) =>
        c.id === content.id ? { ...c, status: newStatus } : c
      )
    );

    // 2. Background API update
    realAPI.content
      .updateContentStatus(content.id, newStatus)
      .then(() => {
        success(`Content status updated to ${newStatus === 'active' ? 'Live' : 'Unpublished'}`, 'Status change applied successfully');
      })
      .catch((err) => {
        console.error("Failed to update status:", err);
        error("Failed to update on server", "The change has been reverted");

        // 3. Revert UI
        setContentList((prev) =>
          prev.map((c) =>
            c.id === content.id ? { ...c, status: content.status } : c
          )
        );
      });
  };

  const handleDeleteContent = (content: ContentItem) => {
    setContentToDelete(content);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!contentToDelete) return;

    setIsDeleting(true);

    try {
      await realAPI.content.deleteContent(contentToDelete.id);
      await fetchContent();
      setIsDeleteModalOpen(false);
      setContentToDelete(null);
      success('Content deleted successfully', 'Content has been removed');
    } catch (err) {
      console.error('Failed to delete content:', err);
      error('Failed to delete content', 'Please try again later');
    } finally {
      setIsDeleting(false);
    }
  };

  /* ------------------------------ HELPERS ------------------------------ */
  const formatContentType = (type: string): string => {
    switch (type) {
      case 'privacy_policy':
        return 'Privacy Policy';
      case 'term_and_condition':
        return 'Terms & Conditions';
      case 'other':
        return 'Other (Image)';
      default:
        return type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  if (isNotFound) {
    return (
      <>
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4 sm:mb-0">Content Management</h1>

          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">

            {/* Add Content Button */}
            <Button
              onClick={handleAddContent}
              className="px-4 h-10"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Content
            </Button>
          </div>
        </div>

        {/* Table wrapper */}
        <Card>
          <div className="table-container">
            <div className="table-min-w">
              {/* Grid Header */}
              <div className="grid grid-cols-6 gap-3 p-4 pl-4 rounded-t-xl font-semibold text-gray-700 text-sm border-b border-gray-200 -ml-2 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm">
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  style={{ paddingLeft: '10px' }}
                >
                  Name
                </div>
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                >
                  Type
                </div>
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                >
                  Publish Date
                </div>
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                >
                  Last Updated
                </div>
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  style={{ paddingLeft: '10px' }}
                >
                  Status
                </div>
                <div className="text-center">Actions</div>
              </div>
              {/* No Content Found */}
              <div className="text-center py-12 bg-gray-50 rounded-b-xl">
                <img
                  src="/NoRecord.svg"
                  alt="No records found"
                  className="mx-auto mb-4"
                />
                <p className="text-gray-500 mb-4">No content found</p>
                <Button onClick={handleAddContent} className="mt-4">
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Content
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Add Content Modal */}
        <Modal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          title="Add New Content"
          size="2xl"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
                Content Type
              </label>
              <select
                value={newContentType}
                onChange={(e) => setNewContentType(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="term_and_condition">Terms & Conditions</option>
                <option value="privacy_policy">Privacy Policy</option>
                <option value="other">Other (Image)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
                Content Name
              </label>
              <input
                type="text"
                value={newContentName}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Enter content name..."
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${formErrors.name
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300'
                  }`}
              />
              {formErrors.name && (
                <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">
                {newContentType === 'other' ? 'Content Image' : 'Content Body'}
              </label>

              {newContentType === 'other' ? (
                /* Image Upload for 'other' content type */
                <div>
                  <div className="mt-1 flex items-center space-x-4">
                    {uploadedImage ? (
                      <div className="relative">
                        <img
                          src={uploadedImage}
                          alt="Content preview"
                          className="h-64 w-64 object-cover rounded-lg border border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => setUploadedImage(null)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="h-40 w-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                        <Upload className="w-12 h-12 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="content-image-upload"
                      />
                      <label
                        htmlFor="content-image-upload"
                        className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        {isUploading ? 'Uploading...' : uploadedImage ? 'Change Image' : 'Upload Image'}
                      </label>
                      <p className="text-xs text-gray-500 mt-1">JPEG, PNG, GIF, WebP up to 5MB</p>
                    </div>
                  </div>
                </div>
              ) : (
                /* Rich Text Editor for text content types */
                <>
                  {/* Rich Text Editor Toolbar */}
                  <div className="border border-gray-300 rounded-t-lg bg-gray-50 p-2 flex flex-wrap gap-1">
                    <button
                      type="button"
                      onClick={() => execCommand('bold')}
                      className="p-2 hover:bg-gray-200 rounded transition-colors"
                      title="Bold"
                    >
                      <Bold className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand('italic')}
                      className="p-2 hover:bg-gray-200 rounded transition-colors"
                      title="Italic"
                    >
                      <Italic className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand('underline')}
                      className="p-2 hover:bg-gray-200 rounded transition-colors"
                      title="Underline"
                    >
                      <Underline className="w-4 h-4" />
                    </button>

                    <div className="w-px bg-gray-300 mx-1"></div>

                    <button
                      type="button"
                      onClick={() => execCommand('justifyLeft')}
                      className="p-2 hover:bg-gray-200 rounded transition-colors"
                      title="Align Left"
                    >
                      <AlignLeft className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand('justifyCenter')}
                      className="p-2 hover:bg-gray-200 rounded transition-colors"
                      title="Align Center"
                    >
                      <AlignCenter className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand('justifyRight')}
                      className="p-2 hover:bg-gray-200 rounded transition-colors"
                      title="Align Right"
                    >
                      <AlignRight className="w-4 h-4" />
                    </button>

                    <div className="w-px bg-gray-300 mx-1"></div>

                    <button
                      type="button"
                      onClick={() => execCommand('insertUnorderedList')}
                      className="p-2 hover:bg-gray-200 rounded transition-colors"
                      title="Bullet List"
                    >
                      <List className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand('insertOrderedList')}
                      className="p-2 hover:bg-gray-200 rounded transition-colors"
                      title="Numbered List"
                    >
                      <ListOrdered className="w-4 h-4" />
                    </button>

                    <div className="w-px bg-gray-300 mx-1"></div>

                    <button
                      type="button"
                      onClick={() => execCommand('createLink', 'https://example.com')}
                      className="p-2 hover:bg-gray-200 rounded transition-colors"
                      title="Insert Link"
                    >
                      <Link className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsRichTextImageModalOpen(true)}
                      className="p-2 hover:bg-gray-200 rounded transition-colors"
                      title="Insert Image"
                    >
                      <Image className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand('formatBlock', '<pre>')}
                      className="p-2 hover:bg-gray-200 rounded transition-colors"
                      title="Code Block"
                    >
                      <Code className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Rich Text Editor Content Area */}
                  <div
                    ref={editorRef}
                    contentEditable
                    onInput={handleContentChange}
                    className={`rich-text-editor min-h-[300px] p-4 border border-t-0 rounded-b-lg focus:outline-none focus:ring-2 bg-white ${formErrors.content
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-gray-300 focus:ring-primary-500'
                      }`}
                    style={{ minHeight: '300px' }}
                    data-placeholder="Start typing your content here..."
                  />
                </>
              )}
              {formErrors.content && (
                <p className="mt-1 text-sm text-red-600">{formErrors.content}</p>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => setIsAddModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmAddContent}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Add Content
              </Button>
            </div>
          </div>
        </Modal>
      </>
    );
  }

  return (
    <div>
      {/* Header */}



      <Card className="mb-2 p-[10px]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-900 whitespace-nowrap">
            Content Management
          </h1>

          {/* Actions */}
          <div className="flex justify-end w-full sm:w-auto">
            <Button
              onClick={handleAddContent}
              className="h-10 px-4 w-full sm:min-w-[162px] flex items-center justify-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Content
            </Button>
          </div>
        </div>
      </Card>


      {/* Table wrapper */}
      <Card>
        {isLoading ? (
          <TableShimmer />
        ) : contentList.length === 0 ? (
          <div className="table-container">
            <div className="table-min-w">
              {/* Grid Header */}
              <div className="grid grid-cols-6 gap-3 p-4 pl-4 rounded-t-xl font-semibold text-gray-700 text-sm border-b border-gray-200 -ml-2 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm">
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  style={{ paddingLeft: '10px' }}
                  onClick={() => {
                    if (sortField === 'name') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('name');
                      setSortOrder('asc');
                    }
                    setPage(1);
                  }}
                >
                  Name {sortField === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                </div>
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  onClick={() => {
                    if (sortField === 'type') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('type');
                      setSortOrder('asc');
                    }
                    setPage(1);
                  }}
                >
                  Type {sortField === 'type' && (sortOrder === 'asc' ? '↑' : '↓')}
                </div>
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  onClick={() => {
                    if (sortField === 'createdAt') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('createdAt');
                      setSortOrder('desc');
                    }
                    setPage(1);
                  }}
                >
                  Publish Date {sortField === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                </div>
                <div>Last Updated</div>
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  style={{ paddingLeft: '10px' }}
                  onClick={() => {
                    if (sortField === 'status') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('status');
                      setSortOrder('asc');
                    }
                    setPage(1);
                  }}
                >
                  Status {sortField === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                </div>
                <div className="text-center">Actions</div>
              </div>
              {/* Empty State inside table */}
              <div className="text-center py-12 bg-gray-50 rounded-b-xl">
                <img
                  src="/NoRecord.svg"
                  alt="No records found"
                  className="mx-auto mb-4"
                />
                <p className="text-gray-500 dark:text-gray-400 mb-4">No content found</p>
                <Button onClick={handleAddContent} className="mt-2">
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Content
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="table-container">
            <div className="table-min-w">
              {/* Grid Header */}
              <div className="grid grid-cols-6 gap-3 p-4 pl-4 rounded-t-xl font-semibold text-gray-700 text-sm border-b border-gray-200 -ml-2 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm">
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  style={{ paddingLeft: '10px' }}
                  onClick={() => {
                    if (sortField === 'name') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('name');
                      setSortOrder('asc');
                    }
                    setPage(1);
                  }}
                >
                  Name {sortField === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                </div>
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  onClick={() => {
                    if (sortField === 'type') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('type');
                      setSortOrder('asc');
                    }
                    setPage(1);
                  }}
                >
                  Type {sortField === 'type' && (sortOrder === 'asc' ? '↑' : '↓')}
                </div>
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  onClick={() => {
                    if (sortField === 'createdAt') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('createdAt');
                      setSortOrder('desc');
                    }
                    setPage(1);
                  }}
                >
                  Publish Date {sortField === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                </div>
                <div>Last Updated</div>
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  style={{ paddingLeft: '10px' }}
                  onClick={() => {
                    if (sortField === 'status') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('status');
                      setSortOrder('asc');
                    }
                    setPage(1);
                  }}
                >
                  Status {sortField === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                </div>
                <div className="text-center">Actions</div>
              </div>

              {/* Grid Body */}
              <div className="divide-y divide-gray-200">
                {contentList.map((content) => (
                  <div
                    key={content.id}
                    className="grid grid-cols-6 gap-4 p-3 hover:bg-blue-50 dark:hover:bg-gray-700/50 transition-colors items-center cursor-pointer"
                    onClick={() => handleEditContent(content)}
                  >
                    <div className="flex items-center gap-2 truncate">
                      {content.type === 'other' ? (
                        <Image className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      ) : (
                        <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      )}
                      <span className="truncate font-medium">{content.name}</span>
                    </div>

                    <div className="truncate text-sm text-gray-600">
                      {formatContentType(content.type)}
                    </div>

                    <div className="text-sm text-gray-600">
                      {formatDate(content.createdAt)}
                    </div>
                    <div className="text-sm text-gray-600">
                      {formatDate(content.updatedAt || content.createdAt)}
                    </div>

                    <div>
                      <span
                        className={`inline-flex px-3 py-1 text-xs font-semibold rounded-md capitalize ${content.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                          }`}
                      >
                        {content.status === 'active' ? 'Live' : 'Unpublished'}
                      </span>
                    </div>

                    <div onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-center gap-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleStatus(content);
                          }}
                          className={`p-2 rounded-lg transition-colors ${content.status === 'active'
                            ? 'text-green-600 hover:bg-green-50'
                            : 'text-red-600 hover:bg-red-50'
                            }`}
                          title={`Toggle Status`}
                        >
                          {content.status === 'active' ? (
                            <ToggleRight className="w-7 h-7" />
                          ) : (
                            <ToggleLeft className="w-7 h-7" />
                          )}
                        </button>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteContent(content);
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Content"
                        >
                          <Trash2 className="w-6 h-6" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between p-4 border-t border-gray-200 gap-4 sm:gap-0">
                  <p className="text-sm text-gray-600">
                    Showing {(page - 1) * ITEMS_PER_PAGE + 1} to{' '}
                    {Math.min(page * ITEMS_PER_PAGE, total)} of {total} results
                  </p>

                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>

                    <span className="px-4 py-2 text-sm font-medium text-gray-700">
                      Page {page} of {totalPages}
                    </span>

                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Add Content Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add New Content"
        size="2xl"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content Type
            </label>
            <select
              value={newContentType}
              onChange={(e) => setNewContentType(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {!allContent.some(c => c.type === 'term_and_condition') && (
                <option value="term_and_condition">Terms & Conditions</option>
              )}
              {!allContent.some(c => c.type === 'privacy_policy') && (
                <option value="privacy_policy">Privacy Policy</option>
              )}
              <option value="other">Other (Image)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content Name
            </label>
            <input
              type="text"
              value={newContentName}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Enter content name..."
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${formErrors.name
                ? 'border-red-300 focus:ring-red-500'
                : 'border-gray-300'
                }`}
            />
            {formErrors.name && (
              <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {newContentType === 'other' ? 'Content Image' : 'Content Body'}
            </label>

            {newContentType === 'other' ? (
              /* Image Upload for 'other' content type */
              <div>
                <div className="mt-1 flex items-center space-x-4">
                  {uploadedImage ? (
                    <div className="relative">
                      <img
                        src={uploadedImage}
                        alt="Content preview"
                        className="h-40 w-40 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => setUploadedImage(null)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="h-40 w-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                      <Upload className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="content-image-upload-2"
                    />
                    <label
                      htmlFor="content-image-upload-2"
                      className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      {isUploading ? 'Uploading...' : uploadedImage ? 'Change Image' : 'Upload Image'}
                    </label>
                    <p className="text-xs text-gray-500 mt-1">JPEG, PNG, GIF, WebP up to 5MB</p>
                  </div>
                </div>
              </div>
            ) : (
              /* Rich Text Editor for text content types */
              <>
                {/* Rich Text Editor Toolbar */}
                <div className="border border-gray-300 rounded-t-lg bg-gray-50 p-2 flex flex-wrap gap-1">
                  <button
                    type="button"
                    onClick={() => execCommand('bold')}
                    className="p-2 hover:bg-gray-200 rounded transition-colors"
                    title="Bold"
                  >
                    <Bold className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => execCommand('italic')}
                    className="p-2 hover:bg-gray-200 rounded transition-colors"
                    title="Italic"
                  >
                    <Italic className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => execCommand('underline')}
                    className="p-2 hover:bg-gray-200 rounded transition-colors"
                    title="Underline"
                  >
                    <Underline className="w-4 h-4" />
                  </button>

                  <div className="w-px bg-gray-300 mx-1"></div>

                  <button
                    type="button"
                    onClick={() => execCommand('justifyLeft')}
                    className="p-2 hover:bg-gray-200 rounded transition-colors"
                    title="Align Left"
                  >
                    <AlignLeft className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => execCommand('justifyCenter')}
                    className="p-2 hover:bg-gray-200 rounded transition-colors"
                    title="Align Center"
                  >
                    <AlignCenter className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => execCommand('justifyRight')}
                    className="p-2 hover:bg-gray-200 rounded transition-colors"
                    title="Align Right"
                  >
                    <AlignRight className="w-4 h-4" />
                  </button>

                  <div className="w-px bg-gray-300 mx-1"></div>

                  <button
                    type="button"
                    onClick={() => execCommand('insertUnorderedList')}
                    className="p-2 hover:bg-gray-200 rounded transition-colors"
                    title="Bullet List"
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => execCommand('insertOrderedList')}
                    className="p-2 hover:bg-gray-200 rounded transition-colors"
                    title="Numbered List"
                  >
                    <ListOrdered className="w-4 h-4" />
                  </button>

                  <div className="w-px bg-gray-300 mx-1"></div>

                  <button
                    type="button"
                    onClick={() => execCommand('createLink', 'https://example.com')}
                    className="p-2 hover:bg-gray-200 rounded transition-colors"
                    title="Insert Link"
                  >
                    <Link className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsRichTextImageModalOpen(true)}
                    className="p-2 hover:bg-gray-200 rounded transition-colors"
                    title="Insert Image"
                  >
                    <Image className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => execCommand('formatBlock', '<pre>')}
                    className="p-2 hover:bg-gray-200 rounded transition-colors"
                    title="Code Block"
                  >
                    <Code className="w-4 h-4" />
                  </button>
                </div>

                {/* Rich Text Editor Content Area */}
                <div
                  ref={editorRef}
                  contentEditable
                  onInput={handleContentChange}
                  className={`rich-text-editor min-h-[300px] p-4 border border-t-0 rounded-b-lg focus:outline-none focus:ring-2 bg-white ${formErrors.content
                    ? 'border-red-300 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-primary-500'
                    }`}
                  style={{ minHeight: '300px' }}
                  data-placeholder="Start typing your content here..."
                />
              </>
            )}
            {formErrors.content && (
              <p className="mt-1 text-sm text-red-600">{formErrors.content}</p>
            )}

          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setIsAddModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmAddContent}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Add Content
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Content Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Content"
        size="2xl"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content Type
            </label>
            <select
              value={newContentType}
              onChange={(e) => setNewContentType(e.target.value as 'term_and_condition' | 'privacy_policy' | 'other')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              disabled
            >
              <option value="term_and_condition">Terms & Conditions</option>
              <option value="privacy_policy">Privacy Policy</option>
              <option value="other">Other (Image)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content Name
            </label>
            <input
              type="text"
              value={newContentName}
              onChange={(e) => setNewContentName(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${formErrors.name ? 'border-red-500' : 'border-gray-300'
                }`}
            />
            {formErrors.name && (
              <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {newContentType === 'other' ? 'Content Image' : 'Content Body'}
            </label>

            {newContentType === 'other' ? (
              /* Image Upload for 'other' content type */
              <div>
                <div className="mt-1 flex items-center space-x-4">
                  {uploadedImage ? (
                    <div className="relative">
                      <img
                        src={uploadedImage}
                        alt="Content preview"
                        className="h-64 w-64 object-cover rounded-lg border border-gray-200"
                      />
                      <button
                        type="button"
                        onClick={() => setUploadedImage(null)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="h-64 w-64 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center dark:border-gray-600">
                      <Upload className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="content-image-edit"
                    />
                    <label
                      htmlFor="content-image-edit"
                      className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600"
                    >
                      {isUploading ? 'Uploading...' : uploadedImage ? 'Change Image' : 'Upload Image'}
                    </label>
                    <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">JPEG, PNG, GIF, WebP up to 5MB</p>
                  </div>
                </div>
              </div>
            ) : (
              /* Rich Text Editor for text content types */
              <div>
                <div className="border border-gray-300 rounded-lg">
                  <div className="bg-gray-50 border-b border-gray-300 p-2 flex flex-wrap items-center gap-1 dark:bg-gray-700 dark:border-gray-600">
                    <button
                      type="button"
                      onClick={() => execCommand('bold')}
                      className="p-2 text-gray-700 hover:bg-gray-200 rounded dark:text-gray-300 dark:hover:bg-gray-600"
                      title="Bold"
                    >
                      <Bold className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand('italic')}
                      className="p-2 text-gray-700 hover:bg-gray-200 rounded dark:text-gray-300 dark:hover:bg-gray-600"
                      title="Italic"
                    >
                      <Italic className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand('underline')}
                      className="p-2 text-gray-700 hover:bg-gray-200 rounded dark:text-gray-300 dark:hover:bg-gray-600"
                      title="Underline"
                    >
                      <Underline className="w-4 h-4" />
                    </button>
                    <div className="border-l border-gray-300 h-6 mx-1 dark:border-gray-600"></div>
                    <button
                      type="button"
                      onClick={() => execCommand('justifyLeft')}
                      className="p-2 text-gray-700 hover:bg-gray-200 rounded dark:text-gray-300 dark:hover:bg-gray-600"
                      title="Align Left"
                    >
                      <AlignLeft className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand('justifyCenter')}
                      className="p-2 text-gray-700 hover:bg-gray-200 rounded dark:text-gray-300 dark:hover:bg-gray-600"
                      title="Align Center"
                    >
                      <AlignCenter className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand('justifyRight')}
                      className="p-2 text-gray-700 hover:bg-gray-200 rounded dark:text-gray-300 dark:hover:bg-gray-600"
                      title="Align Right"
                    >
                      <AlignRight className="w-4 h-4" />
                    </button>
                    <div className="border-l border-gray-300 h-6 mx-1 dark:border-gray-600"></div>
                    <button
                      type="button"
                      onClick={() => execCommand('insertUnorderedList')}
                      className="p-2 text-gray-700 hover:bg-gray-200 rounded dark:text-gray-300 dark:hover:bg-gray-600"
                      title="Bullet List"
                    >
                      <List className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => execCommand('insertOrderedList')}
                      className="p-2 text-gray-700 hover:bg-gray-200 rounded dark:text-gray-300 dark:hover:bg-gray-600"
                      title="Numbered List"
                    >
                      <ListOrdered className="w-4 h-4" />
                    </button>
                    <div className="border-l border-gray-300 h-6 mx-1 dark:border-gray-600"></div>
                    <button
                      type="button"
                      onClick={() => {
                        const url = prompt('Enter URL:');
                        if (url) {
                          execCommand('createLink', url);
                        }
                      }}
                      className="p-2 text-gray-700 hover:bg-gray-200 rounded dark:text-gray-300 dark:hover:bg-gray-600"
                      title="Insert Link"
                    >
                      <Link className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsRichTextImageModalOpen(true)}
                      className="p-2 text-gray-700 hover:bg-gray-200 rounded dark:text-gray-300 dark:hover:bg-gray-600"
                      title="Insert Image"
                    >
                      <Image className="w-4 h-4" />
                    </button>
                    <div className="border-l border-gray-300 h-6 mx-1 dark:border-gray-600"></div>
                    <button
                      type="button"
                      onClick={() => execCommand('formatBlock', '<pre>')}
                      className="p-2 text-gray-700 hover:bg-gray-200 rounded dark:text-gray-300 dark:hover:bg-gray-600"
                      title="Code Block"
                    >
                      <Code className="w-4 h-4" />
                    </button>
                  </div>
                  <div
                    ref={editorRef}
                    contentEditable
                    className="min-h-[200px] p-3 focus:outline-none"
                    style={{ minHeight: '200px' }}
                    dangerouslySetInnerHTML={{ __html: '' }}
                  />
                </div>
                {formErrors.content && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.content}</p>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setIsEditModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmEditContent}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Update Content
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Content"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete "{contentToDelete?.name}"? This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setIsDeleteModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting' : 'Delete Content'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Rich Text Image Upload Modal */}
      <Modal
        isOpen={isRichTextImageModalOpen}
        onClose={() => {
          setIsRichTextImageModalOpen(false);
          setRichTextImage(null);
        }}
        title="Insert Image"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Image Upload
            </label>
            <div className="mt-1 flex items-center space-x-4">
              {richTextImage ? (
                <div className="relative">
                  <img
                    src={richTextImage}
                    alt="Content preview"
                    className="h-40 w-40 object-cover rounded-lg border border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={() => setRichTextImage(null)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="h-40 w-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                  <Upload className="w-12 h-12 text-gray-400" />
                </div>
              )}
              <div className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleRichTextImageUpload}
                  className="hidden"
                  id="richtext-image-upload"
                />
                <label
                  htmlFor="richtext-image-upload"
                  className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  {isRichTextImageUploading ? 'Uploading...' : richTextImage ? 'Change Image' : 'Upload Image'}
                </label>
                <p className="text-xs text-gray-500 mt-1">JPEG, PNG, GIF, WebP up to 5MB</p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => {
                setIsRichTextImageModalOpen(false);
                setRichTextImage(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleInsertRichTextImage}
              disabled={!richTextImage || isRichTextImageUploading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Insert Image
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
